import { Link, NavLink, useNavigate } from 'react-router';
import { LayoutDashboard, Users, Layers, ScrollText, Mail, Send, Workflow, BarChart3, LogOut } from 'lucide-react';
import { api, setSession } from '@/lib/api';

const NAV: Array<{ href: string; label: string; icon: typeof Users }> = [
  { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/contacts', label: 'Contacts', icon: Users },
  { href: '/segments', label: 'Segments', icon: Layers },
  { href: '/audit', label: 'Audit log', icon: ScrollText },
  { href: '/templates', label: 'Templates', icon: Mail },
  { href: '/campaigns', label: 'Campaigns', icon: Send },
  { href: '/flows', label: 'Flows', icon: Workflow },
  { href: '/users', label: 'Users', icon: Users },
  { href: '/analytics', label: 'Analytics', icon: BarChart3 },
];

export function AppSidebar({ user }: { user: { email: string; name?: string; role: string } }) {
  const navigate = useNavigate();

  async function logout() {
    try {
      await api('/auth/logout', { method: 'POST', auth: false });
    } finally {
      setSession(null);
      navigate('/login', { replace: true });
    }
  }

  return (
    // Sticky + viewport-high so the nav stays put and the account footer is always at the bottom,
    // however long the page content is.
    <aside className="sticky top-0 flex h-screen w-60 shrink-0 flex-col border-r bg-white">
      <Link to="/dashboard" className="flex items-center gap-3 border-b px-5 py-4 transition-colors hover:bg-muted" aria-label="Dashboard">
        <img src="/favicon.svg" alt="" className="size-8 shrink-0" />
        <div>
          <div className="text-lg leading-tight">Facet Flow</div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Growth CRM</div>
        </div>
      </Link>
      <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
        {NAV.map(({ href, label, icon: Icon }) => (
          <NavLink
            key={href}
            to={href}
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm ${
                isActive ? 'bg-[var(--ff-primary-subtle)] text-[var(--ff-primary)]' : 'text-foreground hover:bg-muted'
              }`
            }
          >
            <Icon className="size-4" />
            <span className="flex-1">{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t p-4 text-xs">
        <div className="truncate font-medium">{user.name ?? user.email}</div>
        <div className="truncate text-muted-foreground">
          {user.email} · {user.role}
        </div>
        <button onClick={logout} className="mt-2 flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
          <LogOut className="size-3.5" /> Sign out
        </button>
      </div>
    </aside>
  );
}
